import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AdvancedSortService } from './advanced-sort.service';
import { FilterService } from './filter.service';
import { ColumnOption } from './interfaces';

/**
 * Table search configuration interface (for UI search box)
 * Different from IDataSearchConfig which is for data filtering
 */
export interface ITableSearchConfig {
    searchTerm: string;
    searchFields: string[];
    caseSensitive?: boolean;
    exactMatch?: boolean;
}

/**
 * Centralized service to manage table search, sort, and modal state
 * Use this to avoid duplicating logic across components
 */
@Injectable({
    providedIn: 'root'
})
export class TableSearchManagerService {

    // Store for each component's state (keyed by component name)
    private stateStore = new Map<string, ComponentTableState>();

    constructor(
        private advancedSortService: AdvancedSortService,
        private filterService: FilterService
    ) { }

    /**
     * Initialize table search manager for a component
     * @param componentKey Unique key for this component (e.g., 'vendors', 'employees')
     * @param sourceData$ Observable of the source data
     * @param availableColumns Column configuration for search/sort
     * @returns Manager object with all needed properties and methods
     */
    initialize<T>(
        componentKey: string,
        sourceData$: Observable<T[]>,
        availableColumns: ColumnOption[]
    ): TableSearchManager<T> {

        // Create or get state for this component
        if (!this.stateStore.has(componentKey)) {
            this.stateStore.set(componentKey, {
                advancedSearchConfig$: new BehaviorSubject<any>(null),
                isSortModalOpen: false,
                focusedColumn: null
            });
        }

        const state = this.stateStore.get(componentKey)!;

        // Create filtered data observable
        const filteredData$ = combineLatest([
            sourceData$,
            state.advancedSearchConfig$
        ]).pipe(
            map(([data, searchConfig]) => {
                if (!data || data.length === 0) return [];

                let result = [...data]; // Create a copy to avoid mutating original

                // 1. Apply filters first
                if (searchConfig && searchConfig.filterRules && searchConfig.filterRules.length > 0) {
                    console.log(`[${componentKey}] Applying ${searchConfig.filterRules.length} filter(s)`, searchConfig.filterRules);
                    result = this.filterService.applyFilters(result, searchConfig.filterRules);
                    console.log(`[${componentKey}] After filtering: ${result.length} items`);
                }

                // 2. Apply search
                if (searchConfig && searchConfig.searchTerm) {
                    console.log(`[${componentKey}] Applying search: "${searchConfig.searchTerm}"`);
                    result = this.search(result, {
                        searchTerm: searchConfig.searchTerm,
                        searchFields: searchConfig.searchFields,
                        caseSensitive: searchConfig.caseSensitive,
                        exactMatch: searchConfig.exactMatch
                    });
                    console.log(`[${componentKey}] After search: ${result.length} items`);
                }

                // 3. Apply sorting last
                if (searchConfig && searchConfig.sortRules && searchConfig.sortRules.length > 0) {
                    console.log(`[${componentKey}] Applying ${searchConfig.sortRules.length} sort rule(s)`, searchConfig.sortRules);
                    result = this.applySorting(result, searchConfig.sortRules);
                }

                return result;
            })
        );

        // Return manager object
        return {
            // Observables
            filteredData$,

            // Properties
            get isSortModalOpen() { return state.isSortModalOpen; },
            get availableColumns() { return availableColumns; },
            get focusedColumn() { return state.focusedColumn; },

            // Methods
            openSortModal: () => {
                state.focusedColumn = null;
                state.isSortModalOpen = true;
            },

            openColumnSortModal: (header: any) => {
                const column = availableColumns.find(c => c.name === header.entityName);
                state.focusedColumn = column || null;
                state.isSortModalOpen = true;
            },

            closeSortModal: () => {
                state.isSortModalOpen = false;
                state.focusedColumn = null;
            },

            applyAdvancedSort: (config: any) => {
                console.log(`[${componentKey}] Applied search/sort/filter config:`, config);
                state.advancedSearchConfig$.next(config);
                state.isSortModalOpen = false;
                state.focusedColumn = null;
            },

            clearAdvancedSearch: () => {
                console.log(`[${componentKey}] Cleared advanced search`);
                state.advancedSearchConfig$.next(null);
            },

            // Cleanup
            destroy: () => {
                this.stateStore.delete(componentKey);
            }
        };
    }

    /**
     * Simple search implementation (moved from SearchSortService)
     */
    private search<T>(data: T[], config: ITableSearchConfig): T[] {
        if (!config.searchTerm || config.searchTerm.trim() === '') {
            return data;
        }

        const searchTerm = config.caseSensitive 
            ? config.searchTerm.trim() 
            : config.searchTerm.trim().toLowerCase();

        return data.filter(item => {
            // Search in specified fields
            return config.searchFields.some(field => {
                const value = this.getNestedValue(item, field);

                if (value === null || value === undefined) {
                    return false;
                }

                const stringValue = config.caseSensitive 
                    ? String(value) 
                    : String(value).toLowerCase();

                if (config.exactMatch) {
                    return stringValue === searchTerm;
                } else {
                    return stringValue.includes(searchTerm);
                }
            });
        });
    }

    /**
     * Apply sorting to data array
     */
    private applySorting<T>(data: T[], sortRules: any[]): T[] {
        if (!sortRules || sortRules.length === 0) {
            return data;
        }

        const sortedData = [...data];

        sortedData.sort((a: any, b: any) => {
            for (const rule of sortRules) {
                if (!rule.enabled) continue;

                const aValue = this.getNestedValue(a, rule.columnName);
                const bValue = this.getNestedValue(b, rule.columnName);

                const comparison = this.compareValues(aValue, bValue);

                if (comparison !== 0) {
                    return rule.order === 'asc' ? comparison : -comparison;
                }
            }
            return 0;
        });

        return sortedData;
    }

    /**
     * Compare two values for sorting
     */
    private compareValues(a: any, b: any): number {
        // Handle null/undefined
        if (a == null && b == null) return 0;
        if (a == null) return 1;
        if (b == null) return -1;

        // Handle dates
        if (a instanceof Date && b instanceof Date) {
            return a.getTime() - b.getTime();
        }

        // Handle numbers
        if (typeof a === 'number' && typeof b === 'number') {
            return a - b;
        }

        // Handle strings (case-insensitive)
        const aStr = String(a).toLowerCase();
        const bStr = String(b).toLowerCase();
        return aStr.localeCompare(bStr);
    }

    /**
     * Get nested property value from object
     */
    private getNestedValue(obj: any, path: string): any {
        return path.split('.').reduce((current, prop) => current?.[prop], obj);
    }

    /**
     * Get current state for a component (for debugging)
     */
    getState(componentKey: string): ComponentTableState | undefined {
        return this.stateStore.get(componentKey);
    }

    /**
     * Clear all state (useful for testing)
     */
    clearAll(): void {
        this.stateStore.clear();
    }
}

/**
 * Internal state for each component
 */
interface ComponentTableState {
    advancedSearchConfig$: BehaviorSubject<any>;
    isSortModalOpen: boolean;
    focusedColumn: any;
}

/**
 * Manager returned to component
 */
export interface TableSearchManager<T> {
    // Observables
    filteredData$: Observable<T[]>;

    // Properties (readonly)
    readonly isSortModalOpen: boolean;
    readonly availableColumns: ColumnOption[];
    readonly focusedColumn: any;

    // Methods
    openSortModal(): void;
    openColumnSortModal(header: any): void;
    closeSortModal(): void;
    applyAdvancedSort(config: any): void;
    clearAdvancedSearch(): void;
    destroy(): void;
}

